import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { DeskDocActionRail } from "@/modules/shell/DeskDocActionRail";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  getSalesShipment,
  submitSalesShipment,
  voidSalesShipment,
} from "@/shared/api/sales";
import { notify } from "@/shared/feedback/notify";

export function SalesShipmentDetailPage() {
  const { companySlug, shipmentId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSalesShipment>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    if (!companySlug || !shipmentId) return;
    const d = await getSalesShipment(companySlug, shipmentId);
    setData(d);
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : "Failed";
      if (msg === "Not signed in") navigate("/login", { replace: true });
      else setError(msg);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, shipmentId, navigate]);

  if (!companySlug || !shipmentId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading…</Alert>
        )}
      </PageFrame>
    );
  }

  const { shipment, lines } = data;

  async function onSubmit() {
    setBusy(true);
    setError(null);
    try {
      await submitSalesShipment(companySlug!, shipmentId!);
      notify.success("Submitted");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  async function onVoid() {
    const ok = await confirmUi({
      title: `Void ${shipment.number}?`,
      description: "Frees qty for other shipments. Delivery path unchanged.",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await voidSalesShipment(companySlug!, shipmentId!);
      notify.success("Voided");
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <DeskDocBackLink
        listBasePath={`/c/${companySlug}/sales/in-transit`}
        listLabel="In transit"
      />
      <PageHeader
        eyebrow="In transit"
        title={shipment.number}
        description={`${shipment.customerName} · logistics only (no stock yet)`}
        actions={
          shipment.lifecycle !== "void" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void onVoid()}
            >
              Void
            </Button>
          ) : null
        }
      />
      <div className="mt-2">
        <LifecycleStatus value={shipment.lifecycle} showLabel />
      </div>
      {shipment.lifecycle === "draft" ? (
        <DeskDocActionRail className="mt-4">
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => void onSubmit()}
          >
            Submit
          </Button>
        </DeskDocActionRail>
      ) : null}
      {shipment.lifecycle === "open" ? (
        <DeskDocActionRail className="mt-4">
          <Button
            type="button"
            size="sm"
            onClick={() =>
              navigate(`/c/${companySlug}/sales/orders/${shipment.salesOrderId}`)
            }
          >
            Deliver from order
          </Button>
        </DeskDocActionRail>
      ) : null}
      {error ? (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      ) : null}
      <FieldGroup title="Summary" className="mt-6" level="sub">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-[var(--color-muted)]">Sales order</dt>
          <dd className="m-0">
            <RelatedDocLink
              companySlug={companySlug}
              kind="sales_order"
              docId={shipment.salesOrderId}
            >
              {shipment.salesOrderNumber}
            </RelatedDocLink>
          </dd>
          <dt className="text-[var(--color-muted)]">Doc date</dt>
          <dd className="m-0">{shipment.docDate}</dd>
          {shipment.notes ? (
            <>
              <dt className="text-[var(--color-muted)]">Notes</dt>
              <dd className="m-0">{shipment.notes}</dd>
            </>
          ) : null}
        </dl>
      </FieldGroup>
      <FieldGroup title="Lines" className="mt-4" level="sub">
        <ul className="m-0 list-none space-y-2 p-0">
          {lines.map((l) => (
            <li key={l.id} className="flex justify-between gap-3 text-sm">
              <span>{l.productName}</span>
              <span className="font-mono tabular-nums">{l.qty}</span>
            </li>
          ))}
        </ul>
      </FieldGroup>
    </PageFrame>
  );
}
